/**
 * Browser geolocation.
 *
 * Coordinates are always returned in [lng, lat] order to match GeoJSON and
 * everything the API expects - the opposite of what the browser gives us.
 * Requires a secure context (https, or localhost during development).
 */

export const isSupported = () => typeof navigator !== 'undefined' && 'geolocation' in navigator;

/**
 * The browser has no separate "request permission" call - the prompt appears on
 * the first position request. This resolves what we know without prompting, so
 * callers can keep the same shape as the mobile app.
 */
export const requestLocationPermission = async () => {
  if (!isSupported()) return false;
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      return status.state !== 'denied';
    }
  } catch (e) { /* Safari < 16 has no Permissions API for geolocation */ }
  return true;
};

/** One-shot fix. Resolves null rather than throwing. `fresh` refuses a cached position. */
/**
 * Why the most recent fix failed: 1 permission denied, 2 no position available,
 * 3 timed out, 0 unknown. Kept beside the null return so existing callers are
 * untouched but a screen that wants to explain the failure can.
 */
let lastError = 0;
export const lastPositionError = () => lastError;

export const getCurrentPosition = ({ fresh = false } = {}) => new Promise((resolve) => {
  lastError = 0;
  if (!isSupported()) { resolve(null); return; }
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({
      coordinates: [pos.coords.longitude, pos.coords.latitude],
      accuracy: pos.coords.accuracy,
      heading: pos.coords.heading || 0,
      speedKmph: Math.max(0, Math.round((pos.coords.speed || 0) * 3.6)),
    }),
    (err) => { lastError = (err && err.code) || 0; resolve(null); },
    { enableHighAccuracy: true, timeout: fresh ? 20000 : 15000, maximumAge: fresh ? 0 : 10000 },
  );
});

/** Continuous updates while a driver is on duty. Returns a stop function. */
export const watchPosition = (onUpdate) => {
  if (!isSupported()) return () => {};
  const id = navigator.geolocation.watchPosition(
    (pos) => onUpdate({
      coordinates: [pos.coords.longitude, pos.coords.latitude],
      heading: pos.coords.heading || 0,
      speedKmph: Math.max(0, Math.round((pos.coords.speed || 0) * 3.6)),
    }),
    () => {},
    { enableHighAccuracy: true, maximumAge: 8000, timeout: 20000 },
  );
  return () => navigator.geolocation.clearWatch(id);
};

export const toLatLng = ([lng, lat]) => ({ latitude: lat, longitude: lng });

const location = { isSupported, requestLocationPermission, getCurrentPosition, watchPosition, toLatLng };
export default location;
